/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import MainNavigation from "@/components/layout/main-navigation";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingSectionHeader } from "@/components/landing/marketing-section-header";
import { EmailCaptureForm } from "@/components/landing/email-capture-form";
import { CheckCircle, Clock, Lightbulb } from "lucide-react";
import { FadeIn } from "@/components/landing/primitives/fade-in";

type RoadmapStatus = "completed" | "coming-soon" | "exploring";

interface RoadmapItem {
  title: string;
  description: string;
  status: RoadmapStatus;
}

const roadmapItems: RoadmapItem[] = [
  {
    title: "AI-Powered Cost Matching",
    description:
      "Intelligent matching of takeoff items to your cost database using AI for faster, more accurate estimates.",
    status: "completed",
  },
  {
    title: "Professional Proposal Builder",
    description:
      "Create polished, branded proposals from your estimates with one click.",
    status: "completed",
  },
  {
    title: "Blueprint Analysis",
    description:
      "Upload construction blueprints and extract measurements and materials automatically.",
    status: "coming-soon",
  },
  {
    title: "Accounting Integrations",
    description:
      "Connect your accounting software to sync estimates, invoices, and financial data.",
    status: "coming-soon",
  },
  {
    title: "Supplier Price Integration",
    description:
      "Real-time pricing from your preferred suppliers automatically updated in estimates.",
    status: "coming-soon",
  },
  {
    title: "Photo-to-Estimate",
    description:
      "Upload job site photos and get instant material and labor estimates using computer vision AI.",
    status: "exploring",
  },
  {
    title: "Multi-Location Management",
    description:
      "Manage multiple business locations with separate cost databases and team permissions.",
    status: "exploring",
  },
  {
    title: "Advanced Reporting Dashboard",
    description:
      "Comprehensive analytics on estimate accuracy, win rates, and profitability trends.",
    status: "exploring",
  },
  {
    title: "Mobile App",
    description:
      "Full-featured iOS and Android apps for on-site estimating and project management.",
    status: "exploring",
  },
];

const statusConfig: Record<
  RoadmapStatus,
  { label: string; icon: typeof CheckCircle; color: string; bgColor: string }
> = {
  completed: {
    label: "Shipped",
    icon: CheckCircle,
    color: "text-success",
    bgColor: "bg-success/10",
  },
  "coming-soon": {
    label: "Coming Soon",
    icon: Clock,
    color: "text-secondary",
    bgColor: "bg-secondary/10",
  },
  exploring: {
    label: "Exploring",
    icon: Lightbulb,
    color: "text-muted-foreground",
    bgColor: "bg-muted",
  },
};

function RoadmapCard({ item }: { item: RoadmapItem }) {
  const config = statusConfig[item.status];
  const Icon = config.icon;

  return (
    <div className="card-premium group rounded-2xl border border-border bg-muted/30 p-6">
      <div className="mb-4">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${config.bgColor} ${config.color}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {config.label}
        </span>
      </div>
      <h3 className="mb-2 text-lg font-bold text-foreground font-display">
        {item.title}
      </h3>
      <p className="text-muted-foreground leading-relaxed">
        {item.description}
      </p>
    </div>
  );
}

export function RoadmapPageClient() {
  const shippedItems = roadmapItems.filter((i) => i.status === "completed");
  const comingSoonItems = roadmapItems.filter(
    (i) => i.status === "coming-soon",
  );
  const exploringItems = roadmapItems.filter((i) => i.status === "exploring");

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MainNavigation />

      <main className="flex-1">
        <MarketingPageHero
          variant="dark"
          badge="Product Roadmap"
          title={
            <>
              Building the Future of{" "}
              <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
                Construction Estimation
              </span>
            </>
          }
          description="See what we've shipped, what we're working on, and where we're headed. Your feedback shapes our priorities."
        />

        {/* Roadmap Content */}
        <section className="px-4 py-16 md:py-24 lg:py-32 md:px-8">
          <div className="mx-auto max-w-[1280px]">
            {/* Coming Soon */}
            <div className="mb-16">
              <MarketingSectionHeader
                badge="Coming Soon"
                title="Launching Soon"
                description="Features launching soon after release."
                centered={false}
              />
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {comingSoonItems.map((item, index) => (
                  <FadeIn key={item.title} delay={index * 100}>
                    <RoadmapCard item={item} />
                  </FadeIn>
                ))}
              </div>
            </div>

            {/* Exploring */}
            <div className="mb-16">
              <MarketingSectionHeader
                badge="On the Horizon"
                title="Exploring"
                description="Ideas we're researching and designing."
                centered={false}
              />
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {exploringItems.map((item, index) => (
                  <FadeIn key={item.title} delay={index * 100}>
                    <RoadmapCard item={item} />
                  </FadeIn>
                ))}
              </div>
            </div>

            {/* Shipped */}
            <div>
              <MarketingSectionHeader
                badge="Shipped"
                title="Already Live"
                description="Features already in the product."
                centered={false}
              />
              <div className="grid gap-6 md:grid-cols-2">
                {shippedItems.map((item, index) => (
                  <FadeIn key={item.title} delay={index * 100}>
                    <RoadmapCard item={item} />
                  </FadeIn>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Email Signup */}
        <section className="border-t border-border bg-muted/30 px-4 py-16 md:px-8">
          <FadeIn className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-lg font-bold text-foreground font-display">
              Stay Updated
            </h2>
            <p className="mb-6 text-muted-foreground">
              Get notified when we ship new features. Sign up for updates to be
              the first to know.
            </p>
            <EmailCaptureForm
              variant="inline"
              buttonText="Subscribe to Updates"
            />
          </FadeIn>
        </section>
      </main>

      <Footer />
    </div>
  );
}
